import type { SubscriptionTier } from './dto/subscription'

export const ticketFeeModeValues = ['buyer', 'organizer'] as const
/** `buyer`: the service fee is added on top of the price; `organizer`: it is taken from the payout. */
export type TicketFeeMode = (typeof ticketFeeModeValues)[number]

export type TicketFeeTier = SubscriptionTier

/** Service fee per paid ticket by the organizer's plan: basis points of the price plus a fixed part. */
export const TICKET_FEES: Record<TicketFeeTier, { basisPoints: number; fixedCents: number }> = {
  free: { basisPoints: 500, fixedCents: 59 },
  verein_s: { basisPoints: 400, fixedCents: 49 },
  verein_m: { basisPoints: 400, fixedCents: 49 },
  basic: { basisPoints: 400, fixedCents: 49 },
  premium: { basisPoints: 300, fixedCents: 39 },
}

/** Lowest price Stripe accepts for a paid ticket, in cents. */
export const TICKET_MIN_PAID_PRICE_CENTS = 50

/** The service fee for one ticket of `priceCents`; free tickets cost nothing. */
export function ticketFeeCents(priceCents: number, tier: TicketFeeTier): number {
  if (priceCents <= 0) return 0
  const fee = TICKET_FEES[tier]
  return Math.round((priceCents * fee.basisPoints) / 10_000) + fee.fixedCents
}

export type TicketOrderAmounts = {
  unitPriceCents: number
  feePerTicketCents: number
  subtotalCents: number
  feeCents: number
  totalCents: number
  applicationFeeCents: number
  organizerNetCents: number
}

/**
 * What an order costs the buyer and how the charge splits between Pulsify and the organizer.
 * In organizer mode the fee can never take more than the whole charge.
 */
export function ticketOrderAmounts(input: {
  priceCents: number
  quantity: number
  tier: TicketFeeTier
  feeMode: TicketFeeMode
}): TicketOrderAmounts {
  const feePerTicketCents = ticketFeeCents(input.priceCents, input.tier)
  const subtotalCents = input.priceCents * input.quantity
  const feeCents = feePerTicketCents * input.quantity
  const totalCents = input.feeMode === 'buyer' ? subtotalCents + feeCents : subtotalCents
  const applicationFeeCents = Math.min(feeCents, totalCents)
  return {
    unitPriceCents: input.priceCents,
    feePerTicketCents,
    subtotalCents,
    feeCents,
    totalCents,
    applicationFeeCents,
    organizerNetCents: totalCents - applicationFeeCents,
  }
}
